import { Component, Optional } from '@angular/core';
import { ChangeService } from '../services/change.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../services/user.service';
import { DatabaseService } from '../services/database.service';
import { DuplicateDialogComponent, DuplicateDialogData } from './duplicate-dialog/duplicate-dialog.component';
import { PeopleService } from '../services/people.service';
import { firstValueFrom } from 'rxjs';

type ChangeExportRecord = {
  operation: string;
  value: string;
  type: string;
  parent: string;
  description: string;
  asaMgmtIP: string;
  asaGroup: string;
};

interface SearchSuggestion {
  change: any;
  primary: string;
  secondary?: string;
  rank: number;
}

interface CandidateEntry {
  value: any;
  label: string;
  rank: number;
}

interface SuggestionMatch {
  label: string;
  value: string;
  rank: number;
}

@Component({
  selector: 'app-change-control',
  standalone: false,
  templateUrl: './change-control.component.html',
  styleUrl: './change-control.component.scss'
})
export class ChangeControlComponent {
  LOCALONLYTESTING = false;

  private readonly interfaceIpsBySerial = new Map<string, Promise<string[]>>();


  selectedChange: any;
  expandedCrNumber: string | null = null;
  allChanges: any[] = [];
  user: any

  // Listing state
  changes: any[] = [];
  filteredChanges: any[] = [];
  searchTerm = '';
  searchSuggestions: SearchSuggestion[] = [];
  searchFocused = false;
  statusCounts: Record<string, number> = {};
  readonly statusFilters = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Submitted', value: 'submitted' },
    { label: 'Closed', value: 'closed' },
    { label: 'Cancelled', value: 'cancelled' }
  ];
  selectedStatusFilter = 'all';
  private searchBlurTimeout?: ReturnType<typeof setTimeout>;

  isSubmitting: boolean = false;
  isClosing: boolean = false;
  isCanceling: boolean = false;
  descriptionExpanded: boolean = false;

  // Add property for duplicate validation
  duplicateCheckResults: any = null;
  readonly compareChanges = (a: any, b: any) => (a?.crNumber ?? null) === (b?.crNumber ?? null);

  crNumberValidationError: string | null = null;

  private readonly lastEditedCandidateKeys = [
    'lastEditedDateTime',
    'lastEdited',
    'editedDateTime',
    'updatedDateTime',
    'modifiedDateTime',
    'lastModifiedDateTime'
  ] as const;

  constructor(
    private _changes: ChangeService,
    private _user: UserService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private _db: DatabaseService,
    private _people: PeopleService
  ) {
  }

  ngOnInit() {
    if (this.LOCALONLYTESTING) console.error('Local only testing mode enabled - no backend changes will be made.');
    this._user.user$.subscribe(user => {
      if (user && user !== null) {
        this.user = user;
      }
    });

    this._changes.getAllChanges().subscribe({
      next: (changes: any[]) => {
        this.allChanges = Array.isArray(changes) ? changes : [];
        this.sortChanges();

        // Check if there's a crNumber in the URL parameters
        this.route.queryParams.subscribe(params => {
          const requested = (params['crNumber'] ?? '').toString().trim();
          if (!requested) {
            return;
          }

          const change = this.allChanges.find(c => (c?.crNumber ?? '').toString().trim() === requested);
          if (change) {
            this.selectedChange = change;
            this.expandedCrNumber = (change?.crNumber ?? null) ? (change.crNumber ?? null) : null;
            const normalized = this.normalizeStatus(change);
            if (normalized !== this.selectedStatusFilter && this.selectedStatusFilter !== 'all') {
              this.selectedStatusFilter = normalized;
            }
            this.applyFilters();
          } else {
            console.warn('Change Control: crNumber from URL not found in loaded changes:', requested);
          }
        });
      },
      error: (error) => {
        console.error('Error fetching all changes:', error);
        this.allChanges = [];
        this.refreshDerivedCollections();
      }
    });
  }

  submitChange() {
    // Check for duplicates before submitting
    if (this.selectedChange) {
      if (!this.isChangeEditable(this.selectedChange)) {
        console.error('Submit ignored: change is not in an editable state.');
        return;
      }

      const submitterGmid = (this.user?.gmid ?? '').toString().trim();
      if (!submitterGmid) {
        console.error('Submit ignored: current user GMID not available yet.');
        window.alert('Your user profile is still loading. Please wait a moment and try Submit again.');
        return;
      }

      if (this.isSubmitting) return; // guard
      this.isSubmitting = true;
      console.log('Checking for duplicates before submission...');
      this.checkForDuplicates(this.selectedChange).then(duplicates => {
        if (duplicates.length > 0) {
          this.duplicateCheckResults = duplicates;
          console.error('Duplicates found:', duplicates);

          const dialogData: DuplicateDialogData = {
            duplicates,
            summary: this.getDuplicateSummary(duplicates),
            clipboardText: this.buildDuplicateClipboardText(duplicates)
          };

          const dialogRef = this.dialog.open(DuplicateDialogComponent, {
            width: '720px',
            data: dialogData
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result === 'proceed') {
              this.proceedWithSubmission();
            } else {
              this.isSubmitting = false;
            }

            this.duplicateCheckResults = null;
          });
        } else {
          console.log('No duplicates found, proceeding with submission');
          this.proceedWithSubmission();
        }
      });
    }
  }

  private toTimestamp(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    if (value instanceof Date) {
      const t = value.getTime();
      return Number.isFinite(t) ? t : 0;
    }

    const t = new Date(value).getTime();
    return Number.isFinite(t) ? t : 0;
  }

  private getCreatedTimestamp(change: any): number {
    return this.toTimestamp(change?.createdDateTime ?? change?.createdOn ?? change?.createdAt);
  }

  getLastEditedTimestamp(change: any): number {
    if (!change) {
      return 0;
    }

    for (const key of this.lastEditedCandidateKeys) {
      const t = this.toTimestamp((change as any)[key]);
      if (t) {
        return t;
      }
    }

    // Fallback: if the backend doesn't supply last-edited yet, treat created as last-edited.
    return this.getCreatedTimestamp(change);
  }

  getLastEditedDate(change: any): Date | null {
    const t = this.getLastEditedTimestamp(change);
    return t ? new Date(t) : null;
  }

  sortChanges() {
    this.allChanges.sort((a, b) => {
      const editedA = this.getLastEditedTimestamp(a);
      const editedB = this.getLastEditedTimestamp(b);
      if (editedA !== editedB) {
        return editedB - editedA; // Descending (most recent edits first)
      }

      const createdA = this.getCreatedTimestamp(a);
      const createdB = this.getCreatedTimestamp(b);
      if (createdA !== createdB) {
        return createdB - createdA;
      }

      const crA = (a?.crNumber ?? '').toString();
      const crB = (b?.crNumber ?? '').toString();
      return crA.localeCompare(crB);
    });

    this.refreshDerivedCollections();
  }

  private proceedWithSubmission() {
    if (!this.selectedChange) {
      this.isSubmitting = false;
      return;
    }

    const submitterGmid = (this.user?.gmid ?? '').toString().trim();
    if (!submitterGmid) {
      console.error('Unable to submit: current user GMID not available.');
      window.alert('Unable to submit because your user profile is not available yet. Please refresh and try again.');
      this.isSubmitting = false;
      return;
    }

    const previousEdited = this.selectedChange.lastEditedDateTime;
    this.selectedChange.submissionStatus = 'Submitted';
    this.selectedChange.submittedByGMID = submitterGmid;
    this.selectedChange.submittedDateTime = new Date();
    this.selectedChange.lastEditedDateTime = new Date();
    if (this.LOCALONLYTESTING) {
      this.syncFilterToSelectedChangeIfNeeded();
      this.sortChanges();
      if (Array.isArray(this.selectedChange?.cliConfig) && this.selectedChange.cliConfig.length) {
        this.createAndOuputCSV(this.selectedChange);
      }
      this.isSubmitting = false;
    } else {
      this._changes.submitChange(this.selectedChange).subscribe({
        next: () => {
          this.syncFilterToSelectedChangeIfNeeded();
          this.sortChanges();
          if (Array.isArray(this.selectedChange?.cliConfig) && this.selectedChange.cliConfig.length) {
            this.createAndOuputCSV(this.selectedChange);
          }
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Error submitting change:', error);
          if (this.selectedChange) {
            this.selectedChange.lastEditedDateTime = previousEdited;
          }
          this.isSubmitting = false;
        }
      });
    }
  }

  downloadChangeCsv(change: any) {
    if (!change) return;
    this.createAndOuputCSV(change);
  }

  cancelChange() {
    this.isCanceling = true;
    const previousEdited = this.selectedChange?.lastEditedDateTime;
    this.selectedChange.submissionStatus = 'Cancelled';
    this.selectedChange.lastEditedDateTime = new Date();
    if (this.LOCALONLYTESTING) {
      this.isCanceling = false;
      this.syncFilterToSelectedChangeIfNeeded();
      this.sortChanges();
    } else {
      this._changes.cancelChange(this.selectedChange).subscribe({
        next: () => {
          this.isCanceling = false;
          this.syncFilterToSelectedChangeIfNeeded();
          this.sortChanges();
        },
        error: (error) => {
          console.error('Error cancelling change:', error);
          if (this.selectedChange) {
            this.selectedChange.lastEditedDateTime = previousEdited;
          }
          this.isCanceling = false;
        }
      });
    }
  }

  closeChange() {
    if (!this.selectedChange) return;
    const confirmed = window.confirm('Are you sure you want to close this change?');
    if (!confirmed) {
      return;
    }

    this.isClosing = true;
    const previousEdited = this.selectedChange?.lastEditedDateTime;
    this.selectedChange.submissionStatus = 'Closed';
    this.selectedChange.lastEditedDateTime = new Date();
    if (this.LOCALONLYTESTING) {
      this.isClosing = false;
      this.syncFilterToSelectedChangeIfNeeded();
      this.sortChanges();
    } else {
      this._changes.closeChange(this.selectedChange).subscribe({
        next: () => {
          this.isClosing = false;
          this.syncFilterToSelectedChangeIfNeeded();
          this.sortChanges();
        },
        error: (error) => {
          console.error('Error closing change:', error);
          if (this.selectedChange) {
            this.selectedChange.lastEditedDateTime = previousEdited;
          }
          this.isClosing = false;
        }
      });
    }
  }

  setActive() {
    if (!this.selectedChange) return;
    this.isSubmitting = true;
    const previousEdited = this.selectedChange?.lastEditedDateTime;
    this.selectedChange.submissionStatus = 'Active';
    this.selectedChange.lastEditedDateTime = new Date();

    if (this.LOCALONLYTESTING) {
      this.isSubmitting = false;
      this.syncFilterToSelectedChangeIfNeeded();
      this.sortChanges();
    } else {
      this._changes.activeChange(this.selectedChange).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.syncFilterToSelectedChangeIfNeeded();
          this.sortChanges();
        },
        error: (error) => {
          console.error('Error setting change to active:', error);
          if (this.selectedChange) {
            this.selectedChange.lastEditedDateTime = previousEdited;
          }
          this.isSubmitting = false;
        }
      });
    }
  }

  selectChange(change: any) {
    // Logic to select a change from the list
    console.log('Change selected:', change);
    this.selectedChange = change; // Set the selected change
    this.expandedCrNumber = change?.crNumber ?? null;

    const normalized = this.normalizeStatus(change);
    if (normalized !== this.selectedStatusFilter && this.selectedStatusFilter !== 'all') {
      this.selectedStatusFilter = normalized;
      this.applyFilters();
    }

    // Update URL with crNumber parameter
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { crNumber: change?.crNumber || null, fromCart: null },
      queryParamsHandling: 'merge'
    });
  }

  isRowExpanded(change: any): boolean {
    const crNumber = (change?.crNumber ?? '').toString();
    if (!crNumber) return false;
    return this.expandedCrNumber === crNumber;
  }

  toggleRow(change: any, event?: Event) {
    event?.stopPropagation();
    const crNumber = (change?.crNumber ?? '').toString();
    if (!crNumber) return;

    if (this.expandedCrNumber === crNumber) {
      this.expandedCrNumber = null;
      return;
    }

    this.selectChange(change);
  }

  onCrNumberBlur(newValue: string) {
    if (!this.selectedChange) return;
    const trimmed = (newValue || '').trim();
    if (!trimmed || trimmed === this.selectedChange.crNumber) return;

    const trimmedLower = trimmed.toLowerCase();
    const isDuplicate = (this.allChanges || []).some(c =>
      c !== this.selectedChange &&
      (c?.crNumber ?? '').toString().trim().toLowerCase() === trimmedLower
    );

    if (isDuplicate) {
      this.crNumberValidationError = 'CR already exists';
      return;
    }

    this.crNumberValidationError = null;

    const previousCr = this.selectedChange.crNumber;
    const previousEdited = this.selectedChange.lastEditedDateTime;
    this.selectedChange.crNumber = trimmed;
    this.selectedChange.lastEditedDateTime = new Date();
    if (this.expandedCrNumber === previousCr) {
      this.expandedCrNumber = trimmed;
    }

    if (this.LOCALONLYTESTING) {
      this.sortChanges();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { crNumber: trimmed },
        queryParamsHandling: 'merge'
      });
      return;
    }

    this._changes.editChange(this.selectedChange).subscribe({
      next: () => {
        this.sortChanges();
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { crNumber: trimmed },
          queryParamsHandling: 'merge'
        });
      },
      error: (error) => {
        console.error('Error updating Change ID:', error);
        this.selectedChange.crNumber = previousCr;
        this.selectedChange.lastEditedDateTime = previousEdited;
      }
    });
  }

  onDescriptionBlur(newValue: string) {
    if (!this.selectedChange) return;
    const trimmed = (newValue || '').trim();
    if (trimmed === this.selectedChange.itemDescription) return;

    const previousDescription = this.selectedChange.itemDescription;
    const previousEdited = this.selectedChange.lastEditedDateTime;
    this.selectedChange.itemDescription = trimmed;
    this.selectedChange.lastEditedDateTime = new Date();

    if (this.LOCALONLYTESTING) {
      this.sortChanges();
      return;
    }

    this._changes.editChange(this.selectedChange).subscribe({
      next: () => {
        this.sortChanges();
      },
      error: (error) => {
        console.error('Error updating description:', error);
        this.selectedChange.itemDescription = previousDescription;
        this.selectedChange.lastEditedDateTime = previousEdited;
      }
    });
  }

  async createAndOuputCSV(changeIn: any) {
    if (!Array.isArray(changeIn?.cliConfig) || !changeIn.cliConfig.length) {
      console.info('No CLI data available for export');
      return;
    }

    // Determine structure type: legacy tree items vs new grouped change summaries
    const records: ChangeExportRecord[] = [];
    const deviceRefs: Array<{ hostname?: string; serialNumber?: string }> = [];

    // Case A: Top-level is already the new grouped structure
    const top = changeIn.cliConfig;
    if (Array.isArray(top) && top.length && top[0] && top[0].groupName !== undefined && top[0].changes !== undefined) {
      for (const groupEntry of top) {
        const ref = this.extractDeviceRef(groupEntry);
        if (ref) deviceRefs.push(ref);

        const groupName = (groupEntry?.groupName ?? '').toString();
        const changes = Array.isArray(groupEntry?.changes) ? groupEntry.changes : [];
        for (const ch of changes) {
          const parsed = await this.parseGroupedChangeToRecord(ch, groupName);
          if (parsed) records.push(parsed);
        }
      }
    } else {
      // Case B: Submissions that contain either grouped or legacy items
      for (const submission of changeIn.cliConfig) {
        const ref = this.extractDeviceRef(submission);
        if (ref) deviceRefs.push(ref);

        const payload = submission?.cliConfig;
        if (Array.isArray(payload) && payload.length && payload[0] && payload[0].groupName !== undefined && payload[0].changes !== undefined) {
          // New grouped structure within submission
          for (const groupEntry of payload) {
            const groupName = (groupEntry?.groupName ?? '').toString();
            const changes = Array.isArray(groupEntry?.changes) ? groupEntry.changes : [];
            for (const ch of changes) {
              const parsed = await this.parseGroupedChangeToRecord(ch, groupName);
              if (parsed) records.push(parsed);
            }
          }
        } else if (Array.isArray(payload)) {
          // Legacy structure: array of CLI tree items
          const legacyFlattened: any[] = [];
          for (const item of payload) {
            await this.extractConfigDataWithAsms(item, legacyFlattened);
          }
          // Map legacy to new CSV schema (operation assumed 'add')
          for (const rec of legacyFlattened) {
            records.push({
              operation: 'add',
              value: rec.value || '',
              type: (rec.type || '').toLowerCase(),
              parent: rec.parent || '',
              description: rec.description || '',
              asaMgmtIP: '',
              asaGroup: ''
            });
          }
        }
      }
    }

    // Ensure new columns exist for grouped-change records.
    for (const rec of records) {
      if ((rec as any).asaMgmtIP === undefined) (rec as any).asaMgmtIP = '';
      if ((rec as any).asaGroup === undefined) (rec as any).asaGroup = '';
    }

    // CSV schema expectations:
    // - group creation rows should use operation 'new' (not 'add')
    // - a group cannot be its own parent
    this.normalizeCsvChangeRecords(records);

    // Append inventory rows at the bottom, after change operations.
    // Requirement: include ALL ASAs that appear in the inventory listing (not only Gold Source).
    const inventoryRows = await this.buildAsaInventoryFooterRows();
    if (inventoryRows.length) {
      // Add a visual separator row for readability in Excel.
      if (records.length) {
        records.push({
          operation: '',
          value: '',
          type: '',
          parent: '',
          description: '',
          asaMgmtIP: '',
          asaGroup: ''
        });
      }
      records.push(...inventoryRows);
    }

    this.triggerCSVDownload(records, `change_${changeIn.crNumber}_export.csv`);
  }

  private normalizeCsvChangeRecords(records: ChangeExportRecord[]) {
    for (const rec of records) {
      if (!rec) continue;

      if ((rec.type ?? '').toString().toLowerCase() === 'group') {
        const parent = (rec.parent ?? '').toString();
        const value = (rec.value ?? '').toString();
        const isSelfParent = !!parent && !!value && parent === value;

        // Only the root group row should be marked as 'new'.
        // Child group rows are still operations within an existing group, so keep 'add'.
        if (isSelfParent) {
          rec.parent = '';
          if ((rec.operation ?? '').toString().toLowerCase() === 'add') {
            rec.operation = 'new';
          }
        }
      }
    }
  }

  private normalizeLookupKey(value: any): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private extractDeviceRef(submission: any): { hostname?: string; serialNumber?: string } | null {
    if (!submission || typeof submission !== 'object') {
      return null;
    }

    const inv = (submission as any).inventory;
    const hostname =
      (submission as any).hostname ??
      (submission as any).hostName ??
      (submission as any).deviceName ??
      (submission as any).asaHostname ??
      inv?.hostname;

    const serialNumber =
      (submission as any).serialNumber ??
      (submission as any).SerialNumber ??
      (submission as any).serial ??
      (submission as any).asaSerialNumber ??
      inv?.serialNumber;

    const hostText = (hostname ?? '').toString().trim();
    const serialText = (serialNumber ?? '').toString().trim();
    if (!hostText && !serialText) {
      return null;
    }
    return { hostname: hostText || undefined, serialNumber: serialText || undefined };
  }

  private async buildAsaInventoryFooterRows(
    _deviceRefs?: Array<{ hostname?: string; serialNumber?: string }>
  ): Promise<ChangeExportRecord[]> {
    let inventory: any[] = [];
    try {
      const res = await firstValueFrom(this._db.getAllInventory());
      inventory = Array.isArray(res) ? res : [];
    } catch (e) {
      console.error('Unable to load inventory for CSV footer rows', e);
      return [];
    }

    // Match the inventory listing sort: Gold Source first, then hostname A→Z.
    const sortedInventory = [...inventory].sort((a, b) => {
      const ag = a && a.masterConfig === 1 ? 1 : 0;
      const bg = b && b.masterConfig === 1 ? 1 : 0;
      if (ag !== bg) return bg - ag;
      const ah = (a?.hostname ?? '').toString().toLowerCase();
      const bh = (b?.hostname ?? '').toString().toLowerCase();
      if (ah < bh) return -1;
      if (ah > bh) return 1;
      return 0;
    });

    const rows = await Promise.all(
      sortedInventory.map(async invItem => {
        const hostname = (invItem?.hostname ?? '').toString().trim();
        if (!hostname) return null;

        const asaGroup = (invItem?.hpnaGroup ?? invItem?.asaGroup ?? invItem?.group ?? '').toString().trim();
        const mgmtFallback = (invItem?.mgmtIpAddress ?? invItem?.managementIp ?? invItem?.managementIpAddress ?? '').toString().trim();

        const serialNumber = (invItem?.serialNumber ?? invItem?.SerialNumber ?? invItem?.serial ?? '').toString().trim();
        const asaMgmtIP = serialNumber
          ? await this.getAllInterfaceIpsForSerial(serialNumber, mgmtFallback)
          : mgmtFallback;

        return {
          operation: '',
          value: hostname,
          type: 'asa',
          parent: '',
          description: '',
          asaMgmtIP,
          asaGroup
        } satisfies ChangeExportRecord;
      })
    );

    return rows.filter((r): r is ChangeExportRecord => !!r);
  }

  private getAllInterfaceIpsForSerial(serialNumber: string, mgmtFallback: string): Promise<string> {
    const serial = (serialNumber ?? '').toString().trim();
    if (!serial) {
      return Promise.resolve(mgmtFallback || '');
    }

    const cached = this.interfaceIpsBySerial.get(serial) ?? this.fetchInterfaceIpsForSerial(serial);
    this.interfaceIpsBySerial.set(serial, cached);

    return cached.then((ifaceIps) => {
      const out: string[] = Array.isArray(ifaceIps) ? [...ifaceIps] : [];

      // Always include inventory mgmt IP as an additional candidate if it looks valid.
      // Sometimes the "correct" IP flips between interfaces, and sometimes the interface
      // endpoint omits or renames the management interface.
      const mgmt = (mgmtFallback ?? '').toString().trim();
      if (mgmt && this.looksLikeIpv4(mgmt) && !out.includes(mgmt)) {
        out.push(mgmt);
      }

      return out.join(' ');
    });
  }

  private fetchInterfaceIpsForSerial(serialNumber: string): Promise<string[]> {
    const serial = (serialNumber ?? '').toString().trim();
    if (!serial) {
      return Promise.resolve([]);
    }

    return (async () => {
      try {
        const res = await firstValueFrom(this._db.getInterfaceBySerial(serial));
        return this.extractInterfaceIpsFromApiResponse(res);
      } catch (e) {
        console.warn('Unable to load interface IPs for CSV export', { serialNumber: serial }, e);
        return [];
      }
    })();
  }

  private extractInterfaceIpsFromApiResponse(rows: any): string[] {
    const list = Array.isArray(rows) ? rows : [];
    const out: string[] = [];
    const seen = new Set<string>();

    for (const row of list) {
      if (!row) continue;
      const ipRaw =
        (row?.interfaceIP ?? row?.interfaceIp ?? row?.ip ?? row?.ipAddress ?? row?.address ?? '').toString().trim();
      if (!ipRaw) continue;
      if (!this.looksLikeIpv4(ipRaw)) continue;
      if (seen.has(ipRaw)) continue;
      seen.add(ipRaw);
      out.push(ipRaw);
    }

    return out;
  }

  private looksLikeIpv4(value: string): boolean {
    const s = (value ?? '').toString().trim();
    if (!s) return false;
    const parts = s.split('.');
    if (parts.length !== 4) return false;
    for (const p of parts) {
      if (!/^\d{1,3}$/.test(p)) return false;
      const n = Number.parseInt(p, 10);
      if (!Number.isFinite(n) || n < 0 || n > 255) return false;
    }
    return true;
  }

  private async extractConfigDataWithAsms(item: any, dataCollection: any[]) {
    if (!item) return;

    // Preserve any description already present on the item.
    let description = (item?.description ?? '').toString();
    if (item.type === 'group') {
      const groupName = item.value || '';
      // ASMS group lookup endpoint is not reliably available in all environments.
      // Use the group name as the description to avoid 404 spam during export.
      if (!description.trim()) {
        description = groupName;
      }
    }

    dataCollection.push({
      value: item.value || '',
      type: item.type || '',
      parent: item.parent || '',
      description
    });

    if (item.children && Array.isArray(item.children)) {
      for (const child of item.children) {
        await this.extractConfigDataWithAsms(child, dataCollection);
      }
    }
  }

  private async parseGroupedChangeToRecord(change: any, parentGroup: string): Promise<ChangeExportRecord | null> {
    if (!change || typeof change.summary !== 'string') {
      return null;
    }
    const s = change.summary.trim();
    // Expect formats like:
    // - "Add fqdn: value"
    // - "Remove host: 1.2.3.4"
    // - "Create group: Name"
    // - "Delete group: Name" (treated as delete; distinct from remove)
    const m = s.match(/^(Add|Remove|Create|Delete)\s+(\w+):\s+(.+)$/i);
    if (!m) {
      return null;
    }
    const opRaw = m[1].toLowerCase();
    const type = m[2].toLowerCase();
    const value = m[3].trim();
    const operation = opRaw === 'create' ? 'add' : opRaw; // treat Create as add

    // Prefer any description provided by the backend/grouping logic.
    // Fall back to the group name for group operations.
    let description = (change?.description ?? change?.itemDescription ?? '').toString().trim();
    if (!description && type === 'group') {
      description = await this.getGroupDescriptionFromAsms(value);
    }

    return { operation, value, type, parent: parentGroup || '', description, asaMgmtIP: '', asaGroup: '' };
  }

  private async getGroupDescriptionFromAsms(groupName: string): Promise<string> {
    // See extractConfigDataWithAsms(): avoid calling ASMS group lookup to prevent 404 spam.
    return groupName || '';
  }

  private triggerCSVDownload(records: ChangeExportRecord[], filename: string) {
    if (!records || records.length === 0) {
      console.error('No data available for export');
      return;
    }

    // CSV column headers per new schema
    const columnHeaders: Array<keyof ChangeExportRecord> = ['operation', 'value', 'type', 'parent', 'description', 'asaMgmtIP', 'asaGroup'];
    
    // Build CSV string
    let csvString = columnHeaders.join(',') + '\n';
    
    // Process each record
    records.forEach(record => {
      const rowValues = columnHeaders.map((header) => {
        const raw = record[header];
        let cellValue = (raw ?? '') as string;
        // Handle special characters in CSV
        if (typeof cellValue === 'string' && (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n'))) {
          cellValue = '"' + cellValue.replace(/"/g, '""') + '"';
        }
        return cellValue;
      });
      csvString += rowValues.join(',') + '\n';
    });

    // Create file and initiate download
    const fileBlob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const downloadLink = document.createElement('a');
    
    if (downloadLink.download !== undefined) {
      const fileURL = URL.createObjectURL(fileBlob);
      downloadLink.setAttribute('href', fileURL);
      downloadLink.setAttribute('download', filename);
      downloadLink.style.visibility = 'hidden';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(fileURL);
    }
  }

  // Duplicate validation methods


  private async checkForDuplicates(changeRequest: any): Promise<any[]> {
    const duplicates: any[] = [];
    
    if (!changeRequest.cliConfig || !Array.isArray(changeRequest.cliConfig)) {
      return duplicates;
    }

    // Check each submission in the change request
    for (const submission of changeRequest.cliConfig) {
      if (submission.cliConfig && Array.isArray(submission.cliConfig)) {
        // Check each CLI config item for duplicates
        for (const cliItem of submission.cliConfig) {
          const foundDuplicates = await this.findDuplicateObjects(cliItem, changeRequest.crNumber, changeRequest);
          duplicates.push(...foundDuplicates);
        }
      }
    }

    return duplicates;
  }

  private async findDuplicateObjects(cliItem: any, currentCrNumber: string, changeRequest: any): Promise<any[]> {
    const duplicates: any[] = [];

    // Check against all other active changes
    for (const change of this.allChanges) {
      if (change.crNumber === currentCrNumber) continue; // Skip self

      if (change.cliConfig && Array.isArray(change.cliConfig)) {
        for (const submission of change.cliConfig) {
          if (submission.cliConfig && Array.isArray(submission.cliConfig)) {
            for (const existingItem of submission.cliConfig) {
              const duplicate = this.compareCliItems(cliItem, existingItem);
              if (duplicate) {
                duplicates.push({
                  type: cliItem.type,
                  value: cliItem.value,
                  parent: cliItem.parent,
                  existingLocation: `CR ${change.crNumber} (Submission ${submission.itemID})`,
                  existingItem: existingItem,
                  severity: duplicate.severity,
                  reason: duplicate.reason
                });
              }
            }
          }
        }
      }
    }

    // Also check for duplicates within the same submission
    const internalDuplicates = this.checkInternalDuplicates(cliItem, changeRequest);
    duplicates.push(...internalDuplicates);

    return duplicates;
  }

  private compareCliItems(item1: any, item2: any): any | null {
    if (!item1 || !item2) return null;

    // Exact match - same type, value, and parent
    if (item1.type === item2.type && 
        item1.value === item2.value && 
        item1.parent === item2.parent) {
      return {
        severity: 'high',
        reason: 'Exact duplicate (same type, value, and parent)'
      };
    }

    // Same value but different type (potential conflict)
    if (item1.value === item2.value && item1.type !== item2.type) {
      return {
        severity: 'medium',
        reason: `Same value but different types (${item1.type} vs ${item2.type})`
      };
    }

    // For network objects, check for overlapping ranges
    if (item1.type === 'subnet' && item2.type === 'subnet') {
      if (this.checkSubnetOverlap(item1.value, item2.value)) {
        return {
          severity: 'medium',
          reason: 'Overlapping subnet ranges'
        };
      }
    }

    // For groups, check if they have the same name but different parents
    if (item1.type === 'group' && item2.type === 'group' && 
        item1.value === item2.value && item1.parent !== item2.parent) {
      return {
        severity: 'low',
        reason: 'Same group name in different parents'
      };
    }

    return null;
  }

  private checkInternalDuplicates(cliItem: any, changeRequest: any): any[] {
    const duplicates: any[] = [];
    const seenItems = new Set<string>();

    if (changeRequest.cliConfig && Array.isArray(changeRequest.cliConfig)) {
      for (const submission of changeRequest.cliConfig) {
        if (submission.cliConfig && Array.isArray(submission.cliConfig)) {
          for (const item of submission.cliConfig) {
            const itemKey = `${item.type}:${item.value}:${item.parent}`;
            
            if (seenItems.has(itemKey)) {
              duplicates.push({
                type: item.type,
                value: item.value,
                parent: item.parent,
                existingLocation: `Same change request (CR ${changeRequest.crNumber})`,
                existingItem: item,
                severity: 'high',
                reason: 'Duplicate within same change request'
              });
            } else {
              seenItems.add(itemKey);
            }
          }
        }
      }
    }

    return duplicates;
  }

  private checkSubnetOverlap(subnet1: string, subnet2: string): boolean {
    try {
      // Parse subnet strings like "192.168.1.0 255.255.255.0"
      const [ip1, mask1] = subnet1.split(' ');
      const [ip2, mask2] = subnet2.split(' ');
      
      if (!ip1 || !mask1 || !ip2 || !mask2) return false;

      // Convert to binary for comparison (simplified check)
      // In a real implementation, you'd use a proper IP library
      const network1 = this.getNetworkAddress(ip1, mask1);
      const network2 = this.getNetworkAddress(ip2, mask2);
      
      return network1 === network2;
    } catch (error) {
      console.error('Error checking subnet overlap:', error);
      return false;
    }
  }

  private getNetworkAddress(ip: string, mask: string): string {
    // Simplified network address calculation
    // In production, use a proper IP calculation library
    const ipParts = ip.split('.').map(Number);
    const maskParts = mask.split('.').map(Number);
    
    const networkParts = ipParts.map((part, index) => part & maskParts[index]);
    return networkParts.join('.');
  }

  // Method to get duplicate summary for display
  getDuplicateSummary(duplicates: any[] = this.duplicateCheckResults): string {
    if (!duplicates || duplicates.length === 0) {
      return 'No duplicates found';
    }

    const high = duplicates.filter((d: any) => d.severity === 'high').length;
    const medium = duplicates.filter((d: any) => d.severity === 'medium').length;
    const low = duplicates.filter((d: any) => d.severity === 'low').length;

    return `Found ${duplicates.length} potential duplicates (${high} high, ${medium} medium, ${low} low priority)`;
  }

  buildDuplicateClipboardText(duplicates: any[] = this.duplicateCheckResults): string {
    if (!duplicates?.length) {
      return 'No duplicates found';
    }

    const header = this.getDuplicateSummary(duplicates);
    const lines = duplicates.map((d: any, index: number) => {
      const parts = [
        `#${index + 1}`,
        `Type: ${d.type ?? '—'}`,
        `Value: ${d.value ?? '—'}`,
        `Parent: ${d.parent ?? '—'}`,
        `Severity: ${d.severity ?? '—'}`,
        `Reason: ${d.reason ?? '—'}`,
        `Existing: ${d.existingLocation ?? '—'}`
      ];
      return parts.join(' | ');
    });

    return [header, ...lines].join('\n');
  }

  // TrackBy for performance when iterating changes list
  trackByCr(index: number, item: any) {
    return item?.crNumber || index;
  }

  getStatusBadge(change: any): { label: string; className: string } {
    const normalized = this.normalizeStatus(change);
    switch (normalized) {
      case 'submitted':
        return { label: 'Submitted', className: 'badge-submitted' };
      case 'closed':
      case 'completed':
      case 'resolved':
        return { label: 'Closed', className: 'badge-closed' };
      case 'cancelled':
      case 'canceled':
        return { label: 'Cancelled', className: 'badge-cancelled' };
      default:
        return { label: 'Active', className: 'badge-open' };
    }
  }

  normalizeStatus(change: any): string {
    if (!change) {
      return 'active';
    }

    const raw = (change.submissionStatus ?? change.status ?? change.state ?? '').toString().trim().toLowerCase();
    if (raw === 'active') {
      return 'active';
    }

    return raw || 'active';
  }

  getStatusCount(filterValue: string): number {
    return this.statusCounts[filterValue] ?? 0;
  }

  setStatusFilter(filterValue: string) {
    if (this.selectedStatusFilter === filterValue) {
      return;
    }
    this.selectedStatusFilter = filterValue;
    this.applyFilters();
  }

  onSearchTermChange(term: string) {
    this.searchTerm = term ?? '';
    if (this.searchFocused) {
      this.updateSearchSuggestions();
    }
    this.applyFilters();
  }

  onSearchFocus() {
    this.clearSearchBlurTimeout();
    this.searchFocused = true;
    if (this.searchTerm.trim()) {
      this.updateSearchSuggestions();
    }
  }

  onSearchBlur() {
    this.clearSearchBlurTimeout();
    this.searchFocused = false;
    this.searchBlurTimeout = setTimeout(() => {
      if (!this.searchFocused) {
        this.clearSearchSuggestions();
      }
      this.searchBlurTimeout = undefined;
    }, 120);
  }

  onSuggestionPointerDown(event: MouseEvent, change: any) {
    event.preventDefault();
    this.onSuggestionSelect(change);
  }

  onSuggestionSelect(change: any) {
    if (!change) {
      return;
    }
    this.searchFocused = false;
    this.clearSearchBlurTimeout();
    this.searchTerm = change?.crNumber?.toString() ?? '';
    const isSameSelection = this.selectedChange && this.compareChanges(this.selectedChange, change);
    this.applyFilters();
    if (!isSameSelection) {
      this.selectChange(change);
    } else {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { crNumber: change?.crNumber || null, fromCart: null },
        queryParamsHandling: 'merge'
      });
    }
    this.clearSearchSuggestions();
  }

  get showSearchSuggestions(): boolean {
    return this.searchFocused && !!this.searchTerm.trim() && this.searchSuggestions.length > 0;
  }

  isChangeEditable(change: any): boolean {
    return this.normalizeStatus(change) === 'active';
  }

  private refreshDerivedCollections() {
    this.statusCounts = this.computeStatusCounts();
    this.applyFilters();
  }

  private computeStatusCounts(): Record<string, number> {
    const counts: Record<string, number> = { all: this.allChanges.length };
    this.allChanges.forEach(change => {
      const status = this.normalizeStatus(change);
      counts[status] = (counts[status] ?? 0) + 1;
    });
    return counts;
  }

  private applyFilters() {
    const previousSelectedCr = this.selectedChange?.crNumber ?? null;
    const filtered = this.allChanges.filter(change => this.matchesStatus(change) && this.matchesSearch(change));
    this.filteredChanges = filtered;
    this.changes = filtered;

    if (this.searchTerm.trim()) {
      if (this.searchFocused) {
        this.updateSearchSuggestions();
      }
    } else {
      this.clearSearchSuggestions();
    }

    if (this.selectedChange && !filtered.some(change => this.compareChanges(change, this.selectedChange))) {
      this.selectedChange = filtered[0] ?? null;
      const crNumber = this.selectedChange?.crNumber ?? null;
      this.expandedCrNumber = crNumber;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { crNumber },
        queryParamsHandling: 'merge'
      });
    }

    const nextSelectedCr = this.selectedChange?.crNumber ?? null;
    if (nextSelectedCr !== previousSelectedCr && nextSelectedCr) {
      this.expandedCrNumber = nextSelectedCr;
    }

    if (this.expandedCrNumber && !filtered.some(change => (change?.crNumber ?? null) === this.expandedCrNumber)) {
      this.expandedCrNumber = null;
    }
  }

  private syncFilterToSelectedChangeIfNeeded() {
    if (!this.selectedChange) {
      return;
    }
    console.log('Syncing status filter to selected change:', this.selectedChange);
    const normalized = this.normalizeStatus(this.selectedChange);
    if (normalized !== this.normalizeStatus(this.selectedStatusFilter) && this.selectedStatusFilter !== 'all') {
      this.selectedStatusFilter = normalized;
      this.applyFilters();
    } else {
      // Even if filter doesn't change, re-apply to ensure visibility
      this.applyFilters();
    }
  }

  private matchesStatus(change: any): boolean {
    if (this.selectedStatusFilter === 'all') {
      return true;
    }
    return this.normalizeStatus(change) === this.selectedStatusFilter;
  }

  private matchesSearch(change: any): boolean {
    if (!this.searchTerm?.trim()) {
      return true;
    }
    const term = this.searchTerm.trim().toLowerCase();
    return this.getSearchCandidates(change).some(value => value.includes(term));
  }

  private getSearchCandidates(change: any): string[] {
    return this.getSearchCandidateEntries(change)
      .filter(entry => entry.value !== null && entry.value !== undefined)
      .map(entry => entry.value.toString().toLowerCase());
  }

  private updateSearchSuggestions() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.clearSearchSuggestions();
      return;
    }

    const matches: SearchSuggestion[] = [];

    this.allChanges.forEach(change => {
      if (!this.matchesStatus(change)) {
        return;
      }
      const match = this.findSearchMatch(change, term);
      if (!match) {
        return;
      }

      const primary = change?.crNumber ? change.crNumber.toString() : match.value;
      const secondary = this.formatSuggestionSecondary(change, match);

      matches.push({
        change,
        primary,
        secondary,
        rank: match.rank
      });
    });

    matches.sort((a, b) => {
      if (a.rank !== b.rank) {
        return a.rank - b.rank;
      }
      const dateA = new Date(a.change?.createdDateTime ?? 0).getTime();
      const dateB = new Date(b.change?.createdDateTime ?? 0).getTime();
      return dateB - dateA;
    });

    this.searchSuggestions = matches.slice(0, 6);
  }

  private clearSearchSuggestions() {
    this.searchSuggestions = [];
  }

  private clearSearchBlurTimeout() {
    if (this.searchBlurTimeout) {
      clearTimeout(this.searchBlurTimeout);
      this.searchBlurTimeout = undefined;
    }
  }

  private findSearchMatch(change: any, term: string): SuggestionMatch | null {
    const entries = this.getSearchCandidateEntries(change)
      .filter(entry => entry.value !== null && entry.value !== undefined)
      .sort((a, b) => a.rank - b.rank);

    for (const entry of entries) {
      const valueString = entry.value.toString();
      if (valueString.toLowerCase().includes(term)) {
        return {
          label: entry.label,
          value: valueString,
          rank: entry.rank
        };
      }
    }

    return null;
  }

  private formatSuggestionSecondary(change: any, match: SuggestionMatch): string | undefined {
    if (match.label === 'Change ID') {
      if (change?.itemDescription) {
        return this.buildSecondaryLabel('Description', change.itemDescription);
      }
      const owner = change?.gmid ?? change?.submittedByGMID ?? change?.submittedByName;
      if (owner) {
        return this.buildSecondaryLabel('Owner', owner);
      }
      return undefined;
    }

    return this.buildSecondaryLabel(match.label, match.value);
  }

  private buildSecondaryLabel(label: string, value: string): string {
    const clean = value ? value.toString().trim() : '';
    const normalized = clean || 'N/A';
    if (normalized.length > 80) {
      return `${label}: ${normalized.slice(0, 77)}...`;
    }
    return `${label}: ${normalized}`;
  }

  private getSearchCandidateEntries(change: any): CandidateEntry[] {
    if (!change) {
      return [];
    }

    const entries: CandidateEntry[] = [
      { value: change.crNumber, label: 'Change ID', rank: 0 },
      { value: change.itemDescription, label: 'Description', rank: 1 },
      { value: change.gmid, label: 'Created By GMID', rank: 2 },
      { value: change.submittedByGMID, label: 'Submitted By GMID', rank: 2 },
      { value: change.submittedByName, label: 'Submitted By', rank: 2 },
      { value: change.submissionStatus, label: 'Status', rank: 3 },
      { value: change.status, label: 'Status', rank: 3 },
      { value: change.state, label: 'Status', rank: 3 }
    ];

    if (Array.isArray(change.cliConfig)) {
      change.cliConfig.forEach((submission: any, idx: number) => {
        const prefix = `Submission ${idx + 1}`;
        entries.push(
          { value: submission?.itemID, label: `${prefix} ID`, rank: 4 },
          { value: submission?.itemDescription, label: `${prefix} Description`, rank: 4 },
          { value: submission?.submittedBy, label: `${prefix} Submitted By`, rank: 5 },
          { value: submission?.submittedByGMID, label: `${prefix} GMID`, rank: 5 },
          { value: submission?.submittedByName, label: `${prefix} Name`, rank: 5 }
        );
      });
    }

    return entries;
  }

  ngOnDestroy() {
    this.clearSearchBlurTimeout();
  }

  getFlattenedCliItems(change: any): Array<{ submissionIndex: number; itemIndex: number; item: any; submission: any }> {
    if (!change?.cliConfig || !Array.isArray(change.cliConfig)) {
      return [];
    }

    const rows: Array<{ submissionIndex: number; itemIndex: number; item: any; submission: any }> = [];
    change.cliConfig.forEach((submission: any, submissionIndex: number) => {
      const items = Array.isArray(submission?.cliConfig) ? submission.cliConfig : [];
      items.forEach((item: any, itemIndex: number) => {
        rows.push({ submissionIndex, itemIndex, item, submission });
      });
    });

    return rows;
  }

  
  editChange() {
    if (!this.selectedChange) return;
    if (!this.isChangeEditable(this.selectedChange)) return;
    const crNumber = (this.selectedChange?.crNumber ?? '').toString().trim();
    if (!crNumber) return;

    this.router.navigate(['/view'], {
      queryParams: { crNumber, editMode: true }
    });
  }

}
